import { components } from "@/api-schema";
import {
  PathsV1BranchSearchPostParametersQueryExtend,
  PathsV1BranchSearchPostParametersQuerySelect,
  PathsV1BrandSearchPostParametersQuerySelect,
  PathsV1CompanySearchPostParametersQuerySelect,
} from "@/api-schema-enums";
import { Can } from "@/casl-provider";
import { useBranchCreateMutation } from "@/common/api/branch/use-branch-create.mutation";
import { useBranchSearchQuery } from "@/common/api/branch/use-branch-search.query";
import { useBrandSearchQuery } from "@/common/api/brand/use-brand-search.query";
import { useCompanySearchQuery } from "@/common/api/company/use-company-search.query";
import { PHONE_REGEX } from "@/common/helpers/regex";
import { toastMutationStatus } from "@/common/helpers/toast-mutation-status";
import { AddressFormFields } from "@/components/address-form-fields/address-form-fields";
import { addressFormDefaultValues, addressFormSchema } from "@/components/address-form-fields/address-form-schema";
import { Button, ButtonColorVariant, ButtonVariant } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { FormCardActions } from "@/components/ui/form-card";
import { Input } from "@/components/ui/input";
import { Loader, LoaderSize, LoaderVariant } from "@/components/ui/loader";
import { MultiSelect } from "@/components/ui/multi-select";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  BranchDunningFormCard,
  branchDunningFormSchemaShape,
} from "@/modules/masterdata/branch/_components/branch-dunning-form-card";
import { BranchIdentifierFormCard } from "@/modules/masterdata/branch/_components/branch-identifier-form-card";
import { BrandRelationsFields } from "@/modules/masterdata/branch/_components/brand-relations-fields";
import { parseBranchClipboardData, toBranchFormValues } from "@/modules/masterdata/branch/branch-clipboard";
import {
  branchIdentifierFormDefaultValues,
  branchIdentifierFormSchema,
  getDuplicateBranchIdentifierKeys,
  toBranchIdentifierDtos,
} from "@/modules/masterdata/branch/branch-identifiers";
import { zodResolver } from "@hookform/resolvers/zod";
import { Building2, ClipboardPaste } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useLocation } from "wouter";
import { z } from "zod";

type AddressInput = components["schemas"]["AddressDto"] & { city?: string | null };
type Company = components["schemas"]["Company"];

export const BranchFormSchema = z.object({
  branchIdentifiers: branchIdentifierFormSchema,
  name: z.string().trim().min(1, "masterdata:BranchNameIsRequired"),
  phone: z.string().regex(PHONE_REGEX, "masterdata:PhoneMustBeValid").or(z.literal("")).optional(),
  email: z.email("masterdata:EmailMustBeValid").or(z.literal("")).optional(),
  ...branchDunningFormSchemaShape,
  signatureTabletEmail: z.email("masterdata:EmailMustBeValid").or(z.literal("")).optional(),
  companyUuid: z.string().min(1, "masterdata:CompanyIsRequired"),
  address: addressFormSchema,
  brands: z.array(z.string()).optional(),
  brandRelations: z
    .array(
      z.object({
        brandUuid: z.string(),
        phone: z.string().regex(PHONE_REGEX, "masterdata:PhoneMustBeValid").or(z.literal("")).optional(),
        fax: z.string().regex(PHONE_REGEX, "masterdata:PhoneMustBeValid").or(z.literal("")).optional(),
        email: z.email("masterdata:EmailMustBeValid").or(z.literal("")).optional(),
      }),
    )
    .optional(),
});

export type BranchFormSchemaType = z.infer<typeof BranchFormSchema>;

export function BranchCreateFormFields() {
  const { t } = useTranslation(["masterdata", "common"]);
  const [_, navigate] = useLocation();
  const { mutateAsync, isPending } = useBranchCreateMutation();
  const [isPasting, setIsPasting] = useState(false);
  const form = useForm<BranchFormSchemaType>({
    resolver: zodResolver(BranchFormSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      branchIdentifiers: branchIdentifierFormDefaultValues,
      name: "",
      phone: "",
      email: "",
      dunningUser: "",
      dunningRepresentative: "",
      accountingDunningClerkId: "",
      signatureTabletEmail: "",
      companyUuid: "",
      brands: [],
      brandRelations: [],
      address: addressFormDefaultValues,
    },
  });

  const watchedBranchIdentifiers = useWatch({
    control: form.control,
    name: "branchIdentifiers",
  });

  const { data: companies, isPending: isCompaniesLoading } = useCompanySearchQuery({
    options: {
      parameters: {
        pageSize: -1,
        select: [
          PathsV1CompanySearchPostParametersQuerySelect.Uuid,
          PathsV1CompanySearchPostParametersQuerySelect.Name,
        ],
      },
    },
  });

  const { data: brands, isPending: isBrandsLoading } = useBrandSearchQuery({
    options: {
      body: {},
      parameters: {
        pageSize: -1,
        select: [
          PathsV1BrandSearchPostParametersQuerySelect.Uuid,
          PathsV1BrandSearchPostParametersQuerySelect.Name,
          PathsV1BrandSearchPostParametersQuerySelect.LogoPath,
        ],
      },
    },
  });

  const { data: existingBranches, isPending: isExistingBranchesLoading } = useBranchSearchQuery({
    options: {
      body: {},
      parameters: {
        pageSize: -1,
        select: [
          PathsV1BranchSearchPostParametersQuerySelect.Uuid,
          PathsV1BranchSearchPostParametersQuerySelect.BranchIdentifiers,
        ],
        extend: [PathsV1BranchSearchPostParametersQueryExtend.BranchIdentifiers],
      },
    },
  });

  const onSubmit = async (values: BranchFormSchemaType) => {
    const latitude = values.address.latitude?.trim();
    const longitude = values.address.longitude?.trim();
    const latitudeNumber = latitude && !Number.isNaN(Number(latitude)) ? Number(latitude) : null;
    const longitudeNumber = longitude && !Number.isNaN(Number(longitude)) ? Number(longitude) : null;

    const address: AddressInput = {
      recipientName: values.address.recipientName?.trim() || null,
      company: values.address.company?.trim() || null,
      street: values.address.street.trim(),
      houseNumber: values.address.houseNumber.trim(),
      houseExtension: values.address.houseExtension?.trim() || null,
      postalCode: values.address.postalCode.trim(),
      city: values.address.city.trim(),
      country_code: values.address.country_code.trim(),
      latitude: latitudeNumber,
      longitude: longitudeNumber,
    };

    const brandRelations = (values.brandRelations ?? [])
      .filter((contact) => contact.brandUuid)
      .map((contact) => ({
        brandUuid: contact.brandUuid,
        phone: contact.phone?.trim() || null,
        fax: contact.fax?.trim() || null,
        email: contact.email?.trim() || null,
      }));

    try {
      await toastMutationStatus({
        mutationFn: () =>
          mutateAsync({
            branchIdentifiers: toBranchIdentifierDtos(values.branchIdentifiers),
            name: values.name.trim(),
            phone: values.phone?.trim() || null,
            email: values.email?.trim() || null,
            dunningUser: values.dunningUser || null,
            dunningRepresentative: values.dunningRepresentative || null,
            accountingDunningClerkId: values.accountingDunningClerkId?.trim() || null,
            signatureTabletEmail: values.signatureTabletEmail?.trim() || null,
            company: values.companyUuid,
            address,
            brandRelations,
          }),
        successMessage: t("masterdata:BranchSuccessfullyCreated"),
        errorMessage: t("masterdata:ErrorCreatingBranch"),
        customErrors: {
          409: t("masterdata:BranchIdentifierConflict"),
        },
      });

      navigate("~/masterdata/branch");
    } catch (error) {
      console.error("Error creating branch:", error);
    }
  };

  const duplicateValidation = useMemo(() => {
    const duplicateKeys = getDuplicateBranchIdentifierKeys(
      existingBranches?.data,
      watchedBranchIdentifiers ?? branchIdentifierFormDefaultValues,
    );

    return {
      duplicateKeys,
      hasErrors: duplicateKeys.size > 0,
      errors: duplicateKeys.size > 0 ? [t("masterdata:BranchIdentifierConflict")] : [],
    };
  }, [existingBranches?.data, t, watchedBranchIdentifiers]);

  const handleSubmitWithValidation = (values: BranchFormSchemaType) => {
    if (duplicateValidation.hasErrors) {
      duplicateValidation.errors.forEach((error) => {
        toast.error(t("common:ValidationError"), {
          description: error,
          duration: 5000,
        });
      });
      return;
    }
    onSubmit(values);
  };

  const handlePasteAllValues = useCallback(async () => {
    setIsPasting(true);

    try {
      const clipboardText = await navigator.clipboard.readText();
      const clipboardData = parseBranchClipboardData(clipboardText);

      if (!clipboardData) {
        toast.error(t("masterdata:InvalidBranchClipboardData"));
        return;
      }

      form.reset(toBranchFormValues(clipboardData));
      toast.success(t("masterdata:BranchDataPastedFromClipboard"));
    } catch {
      toast.error(t("masterdata:ErrorReadingBranchDataFromClipboard"));
    } finally {
      setIsPasting(false);
    }
  }, [form, t]);

  const isSubmitDisabled =
    isPending ||
    isExistingBranchesLoading ||
    duplicateValidation.hasErrors ||
    !form.formState.isValid ||
    isBrandsLoading;

  return (
    <Form {...form}>
      <form className="space-y-6" onSubmit={form.handleSubmit(handleSubmitWithValidation)}>
        <div className="space-y-6">
          <Card className="shadow-none">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                {t("common:BasicInformation")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("common:Name")} *</FormLabel>
                    <FormControl>
                      <Input placeholder={t("masterdata:BranchNameExample")} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("masterdata:BranchPhone")}</FormLabel>
                      <FormControl>
                        <Input placeholder={t("masterdata:BranchPhoneExample")} {...field} value={field.value ?? ""} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("masterdata:BranchEmail")}</FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder={t("masterdata:BranchEmailExample")}
                          {...field}
                          value={field.value ?? ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="signatureTabletEmail"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("masterdata:BranchSignatureTabletEmail")}</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder={t("masterdata:BranchSignatureTabletEmailExample")}
                        {...field}
                        value={field.value ?? ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="companyUuid"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("common:Companies")} *</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value} disabled={isCompaniesLoading}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue
                            placeholder={
                              isCompaniesLoading ? t("masterdata:CompaniesAreLoading") : t("masterdata:SelectCompany")
                            }
                          />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {companies?.data?.map((company: Company) => (
                          <SelectItem key={company.uuid} value={company.uuid}>
                            {company.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex flex-col gap-4">
                <FormField
                  control={form.control}
                  name="brands"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("ram:Brands")}</FormLabel>
                      <FormControl>
                        <MultiSelect
                          options={
                            brands?.data?.map((brand) => ({
                              label: brand.name,
                              value: brand.uuid,
                            })) || []
                          }
                          onValueChange={field.onChange}
                          defaultValue={field.value || []}
                          placeholder={isBrandsLoading ? t("masterdata:BrandsAreLoading") : t("ram:SelectBrands")}
                          disabled={isBrandsLoading}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <BrandRelationsFields brands={brands?.data ?? []} />
              </div>
            </CardContent>
          </Card>

          <BranchDunningFormCard />

          <BranchIdentifierFormCard
            duplicateKeys={duplicateValidation.duplicateKeys}
            isCheckingDuplicates={isExistingBranchesLoading}
          />

          <AddressFormFields />

          <Can I="manage" a="Branch">
            <div className="flex justify-end">
              <Button
                type="button"
                variant={ButtonVariant.Outlined}
                colorVariant={ButtonColorVariant.Secondary}
                onClick={handlePasteAllValues}
                disabled={isPasting}
              >
                {isPasting ? (
                  <Loader size={LoaderSize.Small} variant={LoaderVariant.Wheel} className="mr-2" />
                ) : (
                  <ClipboardPaste className="mr-2 h-4 w-4" />
                )}
                {t("masterdata:PasteAllValuesFromClipboard")}
              </Button>
            </div>
          </Can>

          <FormCardActions
            cancelLabel={t("common:Cancel")}
            onCancel={() => window.history.back()}
            submitDisabled={isSubmitDisabled}
            submitLabel={t("common:Save")}
            isPending={isPending}
          />
        </div>
      </form>
    </Form>
  );
}
